import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Table,
  Modal,
  Form,
  FormControl,
  InputGroup,
  Dropdown,
} from "react-bootstrap";
import BreadCrumb from "Common/BreadCrumb";
import {
  deductStockProduct,
  fetchFullInventoryReport,
  restockProduct,
} from "api/apis";
import CountUp from "react-countup";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

interface VariantAttribute {
  label: string;
  value: string | number;
  valueType: string;
  _id: string;
}

interface ProductVariant {
  sku: string;
  price: number;
  images: string[];
  quantity: number;
  attributes: VariantAttribute[];
  _id: string;
}

interface VariantSKU {
  sku: string;
  currentStock: number;
  totalSold: number;
  _id: string;
}

interface Product {
  _id: string;
  name: string;
  sku: string;
  slug: string;
  images: string[];
  attributeSet: string;
  isSimpleProduct: boolean;
  variants: ProductVariant[];
  __v: number;
}

interface InventoryItem {
  _id: string;
  product: Product;
  variantSKUs: VariantSKU[];
  currentStock: number;
  totalSold: number;
  history: any[];
  lastUpdated: string;
  __v: number;
}

const Inventory = () => {
  document.title = "Inventory";

  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState<"restock" | "deduct" | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [editingItem, setEditingItem] = useState<{
    id: string;
    variantSku?: string;
    productId: string;
    currentStock: number;
    additionalStock: any;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await fetchFullInventoryReport();
        if (response.success) {
          setInventory(response?.data);
        }
      } catch (error: any) {
        toast.error("Failed to fetch inverntory: ", error);
        // console.error("Failed to fetch inventory:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, []);

  const getStatus = (quantity: number) => {
    if (quantity > 5) return "In Stock";
    if (quantity > 0) return "Few Left";
    return "Sold Out";
  };

  const getStatusClass = (quantity: number) => {
    if (quantity > 5) return "success";
    if (quantity > 0) return "warning";
    return "danger";
  };

  // Count variants by status
  const countVariantsByStatus = () => {
    let inStockCount = 0;
    let fewLeftCount = 0;
    let soldOutCount = 0;

    inventory.forEach((item) => {
      if (item.variantSKUs && item.variantSKUs.length > 0) {
        item.variantSKUs.forEach((variant) => {
          const status = getStatus(variant.currentStock);
          if (status === "In Stock") inStockCount++;
          else if (status === "Few Left") fewLeftCount++;
          else soldOutCount++;
        });
      } else {
        const status = getStatus(item.currentStock);
        if (status === "In Stock") inStockCount++;
        else if (status === "Few Left") fewLeftCount++;
        else soldOutCount++;
      }
    });

    return { inStockCount, fewLeftCount, soldOutCount };
  };

  const { inStockCount, fewLeftCount, soldOutCount } = countVariantsByStatus();

  const filteredInventory = inventory.flatMap((item) => {
    // Filter by search term first (product name or SKU)
    const matchesSearch =
      item?.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item?.product?.sku?.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return [];

    // For items with variants
    if (item?.variantSKUs && item?.variantSKUs?.length > 0) {
      const filteredVariants = item?.variantSKUs?.filter((variant) => {
        if (selectedStatus === "All") return true;
        return getStatus(variant.currentStock) === selectedStatus;
      });

      if (filteredVariants.length === 0) return [];

      return [
        {
          ...item,
          variantSKUs: filteredVariants,
        },
      ];
    }

    // For simple products
    if (
      selectedStatus !== "All" &&
      getStatus(item.currentStock) !== selectedStatus
    ) {
      return [];
    }

    return [item];
  });
  const handleEditClick = (
    item: InventoryItem,
    mode: "restock" | "deduct",
    variantSku?: string
  ) => {
    setEditMode(mode);
    setEditingItem({
      id: item._id,
      variantSku: variantSku,
      productId: item.product._id,
      currentStock: variantSku
        ? item.variantSKUs.find((v) => v.sku === variantSku)?.currentStock || 0
        : item.currentStock,
      additionalStock: 0,
    });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  const handleDeductStock = async () => {
    if (!editingItem || editingItem.additionalStock <= 0) return;

    setError(null);
    try {
      const response = await deductStockProduct(
        editingItem.productId,
        editingItem.additionalStock,
        editingItem.variantSku || ""
      );
      if (response.success) {
        setInventory((prev) =>
          prev.map((item) => {
            if (item._id !== editingItem.id) return item;

            if (editingItem.variantSku) {
              return {
                ...item,
                variantSKUs: item.variantSKUs.map((variant) =>
                  variant.sku === editingItem.variantSku
                    ? {
                        ...variant,
                        currentStock:
                          variant.currentStock - editingItem.additionalStock,
                      }
                    : variant
                ),
              };
            }
            return {
              ...item,
              currentStock: item.currentStock - editingItem.additionalStock,
            };
          })
        );
        setEditingItem(null);
        setEditMode(null);
      } else {
        setError(response.message || "Failed to deduct stock");
      }
    } catch (error) {
      setError("Failed to deduct stock. Please try again.");
      console.error("Failed to deduct stock:", error);
    }
  };

  const handleUpdateStock = async () => {
    if (!editingItem || editingItem.additionalStock <= 0) return;

    setError(null);
    try {
      const response = await restockProduct(
        editingItem.productId,
        editingItem.additionalStock,
        editingItem.variantSku || ""
      );
      if (response.success) {
        setInventory((prev) =>
          prev.map((item) => {
            if (item._id !== editingItem.id) return item;

            if (editingItem.variantSku) {
              return {
                ...item,
                variantSKUs: item.variantSKUs.map((variant) =>
                  variant.sku === editingItem.variantSku
                    ? {
                        ...variant,
                        currentStock:
                          variant.currentStock + editingItem.additionalStock,
                      }
                    : variant
                ),
              };
            }
            return {
              ...item,
              currentStock: item.currentStock + editingItem.additionalStock,
            };
          })
        );
        toast.success("Product Stock Updated Successfully")
        setEditingItem(null);
      } else {
        // setError(response.message || "Failed to update stock");
        toast.error(response.message || "Failed to update stock")
      }
    } catch (error) {
      // setError("Failed to update stock. Please try again.");
      toast.error("Failed to update stock. Please try again.");
      // console.error("Failed to update stock:", error);
    }
  };

  interface FormatDateToIST {
    (date: string | number | Date): string;
  }

  const formatDateToIST: FormatDateToIST = (date) => {
    return new Date(date).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
  };

  const getVariantAttributes = (
    variants: ProductVariant[],
    sku: string
  ): string => {
    const variant = variants.find((v) => v.sku === sku);
    if (!variant) return "";

    return variant.attributes
      .map((attr) => `${attr.label}: ${attr.value}`)
      .join(", ");
  };

  const exportToExcel = () => {
    if (!filteredInventory || filteredInventory.length === 0) {
      toast.info("No inventory records found for export.");
      return;
    }
    const exportData = filteredInventory.flatMap((item) => {
      if (item.variantSKUs && item.variantSKUs.length > 0) {
        return item.variantSKUs.map((variant) => ({
          SKU: variant.sku,
          Product: item.product.name,
          Variant: getVariantAttributes(item.product.variants, variant.sku),
          Status: getStatus(variant.currentStock),
          Quantity: variant.currentStock,
          "Sold Quantity": variant.totalSold,
          "Last Updated": formatDateToIST(item.lastUpdated),
        }));
      }
      return {
        SKU: item.product.sku,
        Product: item.product.name,
        Variant: "",
        Status: getStatus(item.currentStock),
        Quantity: item.currentStock,
        "Sold Quantity": item.totalSold,
        "Last Updated": formatDateToIST(item.lastUpdated),
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory");
    XLSX.writeFile(
      wb,
      `inventory_report_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const exportToCSV = () => {
    if (!filteredInventory || filteredInventory.length === 0) {
      // alert("No inventory records found for export.");
      toast.info("No inventory records found for export.");
      return;
    }
    const exportData = filteredInventory.flatMap((item) => {
      if (item.variantSKUs && item.variantSKUs.length > 0) {
        return item.variantSKUs.map((variant) => ({
          SKU: variant?.sku,
          Product: item?.product?.name,
          Variant: getVariantAttributes(item?.product?.variants, variant?.sku),
          Status: getStatus(variant?.currentStock),
          Quantity: variant?.currentStock,
          "Sold Quantity": variant?.totalSold,
          "Last Updated": item?.lastUpdated?.toLocaleString(),
        }));
      }
      return {
        SKU: item?.product?.sku,
        Product: item?.product?.name,
        Variant: "",
        Status: getStatus(item?.currentStock),
        Quantity: item?.currentStock,
        "Sold Quantity": item?.totalSold,
        "Last Updated": item?.lastUpdated,
      };
    });

    const headers = Object.keys(exportData[0]).join(",");
    const rows = exportData
      .map((obj) =>
        Object.values(obj)
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `inventory_report_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Inventory" pageTitle="Configuration" />

          <Row className="row-cols-xxl-3 row-cols-lg-3 row-cols-md-2 row-cols-1">
            <Col>
              <Card className="shadow-sm border-0 overflow-hidden card-animate">
                <div className="position-absolute end-0 start-0 top-0 z-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    version="1.1"
                    width="400"
                    height="250"
                    preserveAspectRatio="none"
                    viewBox="0 0 400 250"
                  >
                    <g mask='url("#SvgjsMask1530")' fill="none">
                      <path
                        d="M209 112L130 191"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1531)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M324 10L149 185"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1532)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M333 35L508 -140"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1532)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M282 58L131 209"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1531)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M290 16L410 -104"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1532)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M216 186L328 74"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1531)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M255 53L176 132"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1531)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M339 191L519 11"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1531)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M95 151L185 61"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1532)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M249 16L342 -77"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1532)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M129 230L286 73"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1531)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M80 216L3 293"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1531)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                    </g>
                    <defs>
                      <mask id="SvgjsMask1530">
                        <rect width="400" height="250" fill="#ffffff"></rect>
                      </mask>
                      <linearGradient
                        x1="100%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                        id="SvgjsLinearGradient1531"
                      >
                        <stop
                          stopColor="rgba(var(--tb-success-rgb), 0)"
                          offset="0"
                        ></stop>
                        <stop
                          stopColor="rgba(var(--tb-success-rgb), 0.1)"
                          offset="1"
                        ></stop>
                      </linearGradient>
                      <linearGradient
                        x1="0%"
                        y1="100%"
                        x2="100%"
                        y2="0%"
                        id="SvgjsLinearGradient1532"
                      >
                        <stop
                          stopColor="rgba(var(--tb-success-rgb), 0)"
                          offset="0"
                        ></stop>
                        <stop
                          stopColor="rgba(var(--tb-success-rgb), 0.1)"
                          offset="1"
                        ></stop>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <Card.Body className="p-4 z-1 position-relative">
                  <div className="d-flex align-items-center gap-3">
                    <div className="flex-shrink-0 avatar-sm">
                      <div className="avatar-title bg-success-subtle text-success fs-3 rounded">
                        <i className="ph-check-circle"></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="fs-22 fw-semibold mb-1">
                        <CountUp
                          start={0}
                          end={inStockCount}
                          duration={3}
                          separator=","
                        />
                      </h4>
                      <p className="mb-0 fw-medium text-uppercase fs-14">
                        In Stock
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="shadow-sm border-0 overflow-hidden card-animate">
                <div className="position-absolute end-0 start-0 top-0 z-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    version="1.1"
                    width="400"
                    height="250"
                    preserveAspectRatio="none"
                    viewBox="0 0 400 250"
                  >
                    <g mask='url("#SvgjsMask1608")' fill="none">
                      <path
                        d="M390 87L269 208"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1609)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M358 175L273 260"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1610)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M319 84L189 214"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1609)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M327 218L216 329"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1610)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M126 188L8 306"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1610)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M220 241L155 306"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1610)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M361 92L427 26"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1609)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M391 188L275 304"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1609)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M178 74L248 4"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1610)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M84 52L-56 192"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1610)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M183 111L247 47"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1610)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M46 8L209 -155"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1609)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                    </g>
                    <defs>
                      <mask id="SvgjsMask1608">
                        <rect width="400" height="250" fill="#ffffff"></rect>
                      </mask>
                      <linearGradient
                        x1="0%"
                        y1="100%"
                        x2="100%"
                        y2="0%"
                        id="SvgjsLinearGradient1609"
                      >
                        <stop
                          stopColor="rgba(var(--tb-warning-rgb), 0)"
                          offset="0"
                        ></stop>
                        <stop
                          stopColor="rgba(var(--tb-warning-rgb), 0.1)"
                          offset="1"
                        ></stop>
                      </linearGradient>
                      <linearGradient
                        x1="100%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                        id="SvgjsLinearGradient1610"
                      >
                        <stop
                          stopColor="rgba(var(--tb-warning-rgb), 0)"
                          offset="0"
                        ></stop>
                        <stop
                          stopColor="rgba(var(--tb-warning-rgb), 0.1)"
                          offset="1"
                        ></stop>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <Card.Body className="p-4 z-1 position-relative">
                  <div className="d-flex align-items-center gap-3">
                    <div className="flex-shrink-0 avatar-sm">
                      <div className="avatar-title bg-warning-subtle text-warning fs-3 rounded">
                        <i className="ph-warning-circle"></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="fs-22 fw-semibold mb-1">
                        <CountUp
                          start={0}
                          end={fewLeftCount}
                          duration={3}
                          separator=","
                        />
                      </h4>
                      <p className="mb-0 fw-medium text-uppercase fs-14">
                        Few Left
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col>
              <Card className="shadow-sm border-0 overflow-hidden card-animate">
                <div className="position-absolute end-0 start-0 bottom-0 z-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    version="1.1"
                    width="400"
                    height="250"
                    preserveAspectRatio="none"
                    viewBox="0 0 400 250"
                  >
                    <g mask='url("#SvgjsMask1551")' fill="none">
                      <path
                        d="M306 65L446 -75"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M399 2L315 86"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M83 77L256 -96"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M281 212L460 33"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M257 62L76 243"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M305 123L214 214"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M327 222L440 109"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                      <path
                        d="M287 109L362 34"
                        strokeWidth="10"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M259 194L332 121"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M376 186L240 322"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M308 153L123 338"
                        strokeWidth="6"
                        stroke="url(#SvgjsLinearGradient1559)"
                        strokeLinecap="round"
                        className="TopRight"
                      ></path>
                      <path
                        d="M218 62L285 -5"
                        strokeWidth="8"
                        stroke="url(#SvgjsLinearGradient1558)"
                        strokeLinecap="round"
                        className="BottomLeft"
                      ></path>
                    </g>
                    <defs>
                      <mask id="SvgjsMask1551">
                        <rect width="400" height="250" fill="#ffffff"></rect>
                      </mask>
                      <linearGradient
                        x1="100%"
                        y1="0%"
                        x2="0%"
                        y2="100%"
                        id="SvgjsLinearGradient1558"
                      >
                        <stop
                          stopColor="rgba(var(--tb-danger-rgb), 0)"
                          offset="0"
                        ></stop>
                        <stop
                          stopColor="rgba(var(--tb-danger-rgb), 0.1)"
                          offset="1"
                        ></stop>
                      </linearGradient>
                      <linearGradient
                        x1="0%"
                        y1="100%"
                        x2="100%"
                        y2="0%"
                        id="SvgjsLinearGradient1559"
                      >
                        <stop
                          stopColor="rgba(var(--tb-danger-rgb), 0)"
                          offset="0"
                        ></stop>
                        <stop
                          stopColor="rgba(var(--tb-danger-rgb), 0.1)"
                          offset="1"
                        ></stop>
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <Card.Body className="p-4 z-1 position-relative">
                  <div className="d-flex align-items-center gap-3">
                    <div className="flex-shrink-0 avatar-sm">
                      <div className="avatar-title bg-danger-subtle text-danger fs-3 rounded">
                        <i className="ph-x-circle"></i>
                      </div>
                    </div>
                    <div>
                      <h4 className="fs-22 fw-semibold mb-1">
                        <CountUp
                          start={0}
                          end={soldOutCount}
                          duration={3}
                          separator=","
                        />
                      </h4>
                      <p className="mb-0 fw-medium text-uppercase fs-14">
                        Sold Out
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Inventory</h5>
                <div className="d-flex flex-column flex-md-row gap-2 align-items-start align-items-md-center mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search by SKU/Name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-100 w-md-50"
                  />
                  <Form.Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="w-100 w-md-25"
                  >
                    <option value="All">All Statuses</option>
                    <option value="In Stock">In Stock</option>
                    <option value="Few Left">Few Left</option>
                    <option value="Sold Out">Sold Out</option>
                  </Form.Select>
                  <Dropdown>
                    <Dropdown.Toggle variant="primary" id="dropdown-export">
                      <i className="ri-download-2-line align-bottom me-1"></i>{" "}
                      Export
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={exportToExcel}>
                        Excel
                      </Dropdown.Item>
                      <Dropdown.Item onClick={exportToCSV}>CSV</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              <Card>
                <Card.Body>
                  {loading ? (
                    <div className="text-center py-4">Loading inventory...</div>
                  ) : (
                    <Table responsive borderless className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "5%" }}>SKU</th>
                          <th style={{ width: "35%" }}>Product</th>
                          <th style={{ width: "20%" }}>Status</th>
                          <th style={{ width: "20%" }}>Quantity</th>
                          <th style={{ width: "20%" }}>Sold Quantity</th>
                          <th className="text-end" style={{ width: "20%" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInventory.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center py-4">
                              No inventory records found.
                            </td>
                          </tr>
                        ) : (
                          filteredInventory.flatMap((item) => {
                            if (
                              item.variantSKUs &&
                              item.variantSKUs.length > 0
                            ) {
                              return item.variantSKUs.map((variant) => (
                                <tr key={`${item._id}-${variant.sku}`}>
                                  <td>{variant.sku}</td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div className="flex-grow-1">
                                        <h6 className="mb-1">
                                          {item.product.name}
                                        </h6>
                                        <small className="text-muted">
                                          {getVariantAttributes(
                                            item.product.variants,
                                            variant.sku
                                          )}
                                        </small>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge bg-${getStatusClass(
                                        variant.currentStock
                                      )}-subtle text-${getStatusClass(
                                        variant.currentStock
                                      )}`}
                                    >
                                      {getStatus(variant.currentStock)}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <span>{variant.currentStock}</span>
                                      {editingItem?.id === item._id &&
                                        editingItem.variantSku ===
                                          variant.sku && (
                                          <div className="d-flex align-items-center gap-1">
                                            {editMode === "restock" ? (
                                              <Button
                                                variant="outline-success"
                                                size="sm"
                                                className="px-2"
                                                disabled
                                              >
                                                +
                                              </Button>
                                            ) : (
                                              <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="px-2"
                                                disabled
                                              >
                                                -
                                              </Button>
                                            )}
                                            <FormControl
                                              type="number"
                                              min="1"
                                              value={
                                                editingItem.additionalStock
                                              }
                                              onChange={(e) =>
                                                setEditingItem({
                                                  ...editingItem,
                                                  additionalStock:
                                                    parseInt(e.target.value) ||
                                                    1,
                                                })
                                              }
                                              style={{ width: "80px" }}
                                              size="sm"
                                            />
                                          </div>
                                        )}
                                    </div>
                                  </td>
                                  <td>{variant.totalSold}</td>
                                  <td className="text-end">
                                    {editingItem?.id === item._id &&
                                    editingItem.variantSku === variant.sku ? (
                                      <div className="d-flex justify-content-end gap-2">
                                        <Button
                                          variant="success"
                                          size="sm"
                                          onClick={
                                            editMode === "restock"
                                              ? handleUpdateStock
                                              : handleDeductStock
                                          }
                                          disabled={
                                            !editingItem.additionalStock ||
                                            editingItem.additionalStock <= 0
                                          }
                                        >
                                          Confirm
                                        </Button>
                                        <Button
                                          variant="secondary"
                                          size="sm"
                                          onClick={handleCancelEdit}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    ) : (
                                      <div className="d-flex justify-content-end gap-2">
                                        <Button
                                          variant="light"
                                          size="sm"
                                          className="text-success"
                                          onClick={() =>
                                            handleEditClick(
                                              item,
                                              "restock",
                                              variant.sku
                                            )
                                          }
                                        >
                                          <i className="ri-add-fill"></i>{" "}
                                          Restock
                                        </Button>
                                        <Button
                                          variant="light"
                                          size="sm"
                                          className="text-danger"
                                          onClick={() =>
                                            handleEditClick(
                                              item,
                                              "deduct",
                                              variant.sku
                                            )
                                          }
                                        >
                                          <i className="ri-subtract-fill"></i>{" "}
                                          Deduct
                                        </Button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ));
                            }
                            return (
                              <tr key={item._id}>
                                <td>{item.product.sku}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                      <h6 className="mb-1">
                                        {item.product.name}
                                      </h6>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span
                                    className={`badge bg-${getStatusClass(
                                      item.currentStock
                                    )}-subtle text-${getStatusClass(
                                      item.currentStock
                                    )}`}
                                  >
                                    {getStatus(item.currentStock)}
                                  </span>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <span>{item.currentStock}</span>
                                    {editingItem?.id === item._id &&
                                      !editingItem.variantSku && (
                                        <div className="d-flex align-items-center gap-1">
                                          {editMode === "restock" ? (
                                            <Button
                                              variant="outline-success"
                                              size="sm"
                                              className="px-2"
                                              disabled
                                            >
                                              +
                                            </Button>
                                          ) : (
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              className="px-2"
                                              disabled
                                            >
                                              -
                                            </Button>
                                          )}
                                          <FormControl
                                            type="number"
                                            min="1"
                                            value={editingItem.additionalStock}
                                            onChange={(e) =>
                                              setEditingItem({
                                                ...editingItem,
                                                additionalStock:
                                                  parseInt(e.target.value) || 1,
                                              })
                                            }
                                            style={{ width: "80px" }}
                                            size="sm"
                                          />
                                        </div>
                                      )}
                                  </div>
                                </td>
                                <td>{item.totalSold}</td>
                                <td className="text-end">
                                  {editingItem?.id === item._id &&
                                  !editingItem.variantSku ? (
                                    <div className="d-flex justify-content-end gap-2">
                                      <Button
                                        variant="success"
                                        size="sm"
                                        onClick={
                                          editMode === "restock"
                                            ? handleUpdateStock
                                            : handleDeductStock
                                        }
                                        disabled={
                                          !editingItem.additionalStock ||
                                          editingItem.additionalStock <= 0
                                        }
                                      >
                                        Confirm
                                      </Button>
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleCancelEdit}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="d-flex justify-content-end gap-2">
                                      <Button
                                        variant="light"
                                        size="sm"
                                        className="text-success"
                                        onClick={() =>
                                          handleEditClick(item, "restock")
                                        }
                                      >
                                        <i className="ri-add-fill"></i> Restock
                                      </Button>
                                      <Button
                                        variant="light"
                                        size="sm"
                                        className="text-danger"
                                        onClick={() =>
                                          handleEditClick(item, "deduct")
                                        }
                                      >
                                        <i className="ri-subtract-fill"></i>{" "}
                                        Deduct
                                      </Button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Inventory;
